import { useState } from 'react';
import { generatePath, useNavigate } from 'react-router';

// our components
import ColumnItem from 'components/DataTable/ColumnItem';
import ColumnVerticalHeader from 'components/DataTable/ColumnVerticalHeader';
import ConfirmArchive from 'components/Modal/KeyActions/ConfirmKeyActionChange';
import DataTable from 'components/DataTable/DataTable';
import Loader from 'components/Loader';
import MortgageRefiKeyAction from 'components/Forms/KeyActions/MortgageRefiKeyAction';
import { PrimaryButton, SecondaryButton } from 'components/Button/Button';
import StateAlert from 'components/Alert/StateAlert';

// Our Hooks
import useGetKeyActions from 'hooks/keyActions/useGetKeyActions';
import useMutateChangeKeyActionStatus from 'hooks/keyActions/useMutateChangeKeyActionStatus';

// Our Utils
import formatKeyActionName from 'shared/utils/formatting/keyaction/formatKeyActionName';
import formatDate from 'shared/utils/date/formatDate';

// Our Routes
import { CLIENT_DYNAMIC_ROUTE } from 'routes';

function ReviewKeyActions() {
	const navigate = useNavigate();
	const [isConfirmArchiveOpen, setIsConfirmArchiveOpen] = useState(false);
	const [isKeyActionOpen, setIsKeyActionOpen] = useState(false);
	const [keyActionInfo, setKeyActionInfo] = useState(null);
	const [isSuccessAlertOpen, setIsSuccessAlertOpen] = useState(false);

	const toggleKeyAction = useMutateChangeKeyActionStatus();

	const {
		data: keyActions,
		isLoading: keyActionsLoading,
		isSuccess: keyActionsSuccess
	} = useGetKeyActions(
		(keyActionData) => {
			return keyActionData.map((keyAction) => ({
				...keyAction,
				clientFullName: `${keyAction.clientFirstName} ${keyAction.clientLastName}`,
				advisorFullName: `${keyAction.advisorFirstName} ${keyAction.advisorLastName}`,
				keyActionName: keyAction.type
			}));
		},
		{
			status: 'REVIEW'
		}
	);

	const clientFullNameKey = 'clientFullName';
	const tradelineIdKey = 'tradelineId';
	const keyActionNameKey = 'keyActionName';
	const advisorFullNameKey = 'advisorFullName';
	const advisorCompanyKey = 'advisorCompanyName';
	const dateQualifiedKey = 'qualifiedDate';

	const columnsAdvisors = [
		ColumnItem(clientFullNameKey, 'Client Full Name', 1, {
			renderHeader: ({ colDef }) => (
				<ColumnVerticalHeader headerName={colDef.headerName} />
			)
		}),
		ColumnItem(tradelineIdKey, 'Tradeline ID', 1, {
			renderHeader: ({ colDef }) => (
				<ColumnVerticalHeader headerName={colDef.headerName} />
			)
		}),
		ColumnItem(keyActionNameKey, 'Key Action Name', 1, {
			renderHeader: ({ colDef }) => (
				<ColumnVerticalHeader headerName={colDef.headerName} />
			),
			valueFormatter: ({ value }) => {
				return formatKeyActionName(value);
			}
		}),
		ColumnItem(dateQualifiedKey, 'Date Qualified', 1, {
			renderHeader: ({ colDef }) => (
				<ColumnVerticalHeader headerName={colDef.headerName} />
			),
			valueFormatter: ({ value }) => {
				return formatDate(value, 'MM/dd/yyyy');
			}
		}),
		ColumnItem(advisorFullNameKey, 'Advisor Full Name', 1, {
			renderHeader: ({ colDef }) => (
				<ColumnVerticalHeader headerName={colDef.headerName} />
			)
		}),
		ColumnItem(advisorCompanyKey, 'Advisor Company', 1, {
			renderHeader: ({ colDef }) => (
				<ColumnVerticalHeader headerName={colDef.headerName} />
			)
		}),
		ColumnItem('actions', 'Client Lookup', 0.5, {
			renderHeader: ({ colDef }) => (
				<ColumnVerticalHeader headerName={colDef.headerName} />
			),
			renderCell: ({ row }) => (
				<PrimaryButton
					onClick={(e) => {
						e.stopPropagation();

						// This is the route that contains the clientId
						const clientRoute = generatePath(CLIENT_DYNAMIC_ROUTE, {
							clientId: row.clientId
						});

						navigate(clientRoute);
					}}
				>
					View
				</PrimaryButton>
			),
			sortable: false
		}),
		ColumnItem('actions-2', 'Archive', 0.5, {
			renderHeader: ({ colDef }) => (
				<ColumnVerticalHeader headerName={colDef.headerName} />
			),
			renderCell: ({ row }) => (
				<SecondaryButton
					onClick={(e) => {
						e.stopPropagation();
						setKeyActionInfo(row);
						setIsConfirmArchiveOpen(true);
					}}
				>
					Archive
				</SecondaryButton>
			),
			sortable: false
		})
	];

	if (keyActionsLoading) {
		return <Loader />;
	}

	if (keyActionsSuccess) {
		const formattedKeyActionName = keyActionInfo?.keyActionName
			? formatKeyActionName(keyActionInfo.keyActionName)
			: '';

		return (
			<>
				{isSuccessAlertOpen && (
					<StateAlert
						variant="success"
						isOpen={isSuccessAlertOpen}
						setIsOpen={setIsSuccessAlertOpen}
						message="Key Action Triggered"
					/>
				)}

				<DataTable
					columns={columnsAdvisors}
					rows={keyActions}
					disableSelectionOnClick
					rowsPerPageOptions={10}
					handleRowClick={(row) => {
						setKeyActionInfo(row);
						setIsKeyActionOpen(true);
					}}
					sortModel={[{ field: dateQualifiedKey, sort: 'desc' }]}
				/>

				{isConfirmArchiveOpen && (
					<ConfirmArchive
						isOpen={isConfirmArchiveOpen}
						handleClose={() => setIsConfirmArchiveOpen(false)}
						keyActionInfo={keyActionInfo}
						title="Are you sure you want to archive key action?"
						subtitle={`This will archive the following key action: ${formattedKeyActionName} for ${keyActionInfo.clientFirstName} ${keyActionInfo.clientLastName}.`}
						primaryButtonText="Archive"
						confirmButtonCallback={() => {
							toggleKeyAction.mutate(
								{
									id: keyActionInfo.id,
									status: 'ARCHIVED'
								},
								{
									onSuccess: () => {
										setIsConfirmArchiveOpen(false);
									}
								}
							);
						}}
						isConfirmButtonLoading={toggleKeyAction.isLoading}
					/>
				)}

				{/* THIS needs to be updated so that it opens a form based on the key action type selected. */}
				{isKeyActionOpen && (
					<MortgageRefiKeyAction
						keyActionInfo={keyActionInfo}
						isOpen={isKeyActionOpen}
						handleClose={() => setIsKeyActionOpen(false)}
					/>
				)}
			</>
		);
	}
}

export default ReviewKeyActions;
